import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { leadGroupService } from '../services/leadGroup.service';
import { messageHandler } from '../services/message-handler.service';
import type { LeadGroup, LeadGroupFormData } from '../types/leadGroup';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import LeadGroupList from '../components/lead-groups/LeadGroupList';
import LeadGroupForm from '../components/lead-groups/LeadGroupForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
}

function LeadGroups() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingLeadGroup, setEditingLeadGroup] = useState<LeadGroup | null>(null);
  const queryClient = useQueryClient();

  const { data: leadGroups, isLoading } = useQuery({
    queryKey: ['lead-groups'],
    queryFn: () => leadGroupService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: LeadGroupFormData) => leadGroupService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-groups'] });
      messageHandler.success('Grupo de leads criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: LeadGroupFormData }) =>
      leadGroupService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-groups'] });
      messageHandler.success('Grupo de leads atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => leadGroupService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-groups'] });
      messageHandler.success('Grupo de leads excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const description = formData.get('description') as string;
    const dailyLimitStr = formData.get('daily_limit') as string;
    const integrationKeyStr = formData.get('integration_key') as string;

    const data: LeadGroupFormData = {
      description,
      daily_limit: dailyLimitStr ? parseInt(dailyLimitStr) : null,
      integration_key: integrationKeyStr ? parseInt(integrationKeyStr) : null,
    };

    if (editingLeadGroup) {
      updateMutation.mutate({ id: editingLeadGroup.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (leadGroup: LeadGroup) => {
    messageHandler.confirm(
      'Excluir Grupo de Leads',
      'Tem certeza que deseja excluir este grupo de leads?',
      () => deleteMutation.mutate(leadGroup.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingLeadGroup(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Grupos de Leads"
          description="Gerencie os grupos de leads para organização e controle de processamento."
          icon={Users}
          actions={
            <Button
              onClick={() => {
                setEditingLeadGroup(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Grupo
            </Button>
          }
        />

        <LeadGroupList
          leadGroups={Array.isArray(leadGroups) ? leadGroups : []}
          onEdit={leadGroup => {
            setEditingLeadGroup(leadGroup);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingLeadGroup ? 'Editar Grupo de Leads' : 'Novo Grupo de Leads'}
        >
          <LeadGroupForm
            leadGroup={editingLeadGroup}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default LeadGroups;
